import React, { useEffect, useState } from "react";
import { Loader2, Layers, Pencil, Trash2, X } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { swatchUrl } from "@/components/studio/tileTexture";

const EMPTY = { name: "", family: "Marmo", material: "", finish: "Matt", tile_w: 600, tile_h: 600, price_m2: "", colour: "", swatch_mode: "material", swatch_m: 1.2, description: "", supplier: "Victoria Plumbing", sku: "", supplier_url: "", image_base64: "" };
const IN = "bg-rossi-bg border border-white/10 rounded-lg px-3 py-2 text-sm text-rossi-cream w-full";
const FIELDS = [["name", "Collection name", 2], ["family", "Family (e.g. Marmo, Pietra)", 1], ["finish", "Finish", 1], ["material", "Material", 2], ["tile_w", "Tile W mm", 1, "number"], ["tile_h", "Tile H mm", 1, "number"], ["price_m2", "Price £/m²", 1, "number"], ["colour", "Colour hex (no image)", 1], ["description", "Description", 2], ["supplier", "Supplier (private)", 1], ["sku", "Supplier SKU (private)", 1], ["supplier_url", "Supplier link (private)", 2]];

export const CollectionsAdmin = () => {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const load = () => api.get("/admin/collections").then((r) => setItems(r.data || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const r = new FileReader();
    r.onload = (ev) => setForm((x) => ({ ...x, image_base64: ev.target.result }));
    r.readAsDataURL(f);
  };
  const save = async () => {
    setSaving(true);
    try {
      const body = { ...form, tile_w: parseInt(form.tile_w, 10) || 600, tile_h: parseInt(form.tile_h, 10) || 600, price_m2: parseFloat(form.price_m2) || 0, swatch_m: parseFloat(form.swatch_m) || 1.2, image_base64: form.image_base64 || null };
      if (editing) await api.put(`/admin/collections/${editing}`, body); else await api.post("/admin/collections", body);
      toast.success(editing ? "Collection updated" : "Collection added");
      setForm(EMPTY); setEditing(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save collection"); }
    finally { setSaving(false); }
  };
  const edit = (c) => { setEditing(c.id); setForm({ ...EMPTY, ...c, image_base64: "" }); };
  const del = async (id) => { if (!window.confirm("Delete this collection?")) return; await api.delete(`/admin/collections/${id}`); load(); };

  return (
    <div className="mt-14" data-testid="collections-admin">
      <div className="flex items-center gap-2 mb-2"><Layers size={20} className="text-rossi-gold" /><h2 className="font-serif text-2xl text-rossi-cream">Tile Collections (Lookbook CMS)</h2></div>
      <p className="text-xs text-rossi-stone mb-6">Tiles shown in the Lookbook and The Studio 3D designer. Supplier fields are private — used only on your order sheets.</p>
      <div className="grid lg:grid-cols-[400px_1fr] gap-6">
        <div className="card-lux p-5">
          <div className="grid grid-cols-2 gap-3">
            {FIELDS.map(([k, ph, span, type]) => <label key={k} className={`text-[11px] text-rossi-stone ${span === 2 ? "col-span-2" : ""}`}>{ph}<input type={type || "text"} placeholder={ph} value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className={`${IN} mt-1`} data-testid={`col-${k}`} /></label>)}
            <select value={form.swatch_mode} onChange={(e) => setForm({ ...form, swatch_mode: e.target.value })} className={IN} data-testid="col-swatch-mode">
              <option value="material">Swatch = material (we draw joints)</option>
              <option value="pattern">Swatch already shows joints</option>
            </select>
            <input type="number" step="0.1" placeholder="Swatch real width m" value={form.swatch_m} onChange={(e) => setForm({ ...form, swatch_m: e.target.value })} className={IN} data-testid="col-swatch-m" />
            <label className="col-span-2 text-xs text-rossi-stone">Swatch image<input type="file" accept="image/*" onChange={onFile} className="mt-1 w-full text-xs text-rossi-cream/70" data-testid="col-image" /></label>
            {(form.image_base64 || form.swatch) && <img src={form.image_base64 || swatchUrl(form.swatch)} alt="swatch" className="h-20 w-20 object-cover rounded-lg" />}
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={save} disabled={saving || !form.name} className="btn-gold flex-1 disabled:opacity-40" data-testid="col-save">{saving ? <Loader2 size={16} className="animate-spin" /> : editing ? "Update collection" : "Add collection"}</button>
            {editing && <button onClick={() => { setEditing(null); setForm(EMPTY); }} className="btn-ghost-gold !px-3" data-testid="col-cancel"><X size={15} /></button>}
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-2 h-fit">
          {items.map((c) => (
            <div key={c.id} className="card-lux p-3 flex items-center gap-3" data-testid={`col-item-${c.id}`}>
              {c.swatch ? <img src={swatchUrl(c.swatch)} alt={c.name} className="h-12 w-12 object-cover rounded" /> : <div className="h-12 w-12 rounded" style={{ background: c.colour }} />}
              <div className="flex-1 min-w-0">
                <p className="text-sm text-rossi-cream truncate">{c.name} <span className="text-xs text-rossi-stone">· {c.family}</span></p>
                <p className="text-[11px] text-rossi-gold font-mono truncate">£{c.price_m2}/m² · {c.supplier} {c.sku}</p>
              </div>
              <button onClick={() => edit(c)} className="text-rossi-stone hover:text-rossi-gold" data-testid={`col-edit-${c.id}`}><Pencil size={15} /></button>
              <button onClick={() => del(c.id)} className="text-rossi-stone hover:text-red-400" data-testid={`col-delete-${c.id}`}><Trash2 size={15} /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CollectionsAdmin;
